import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import TagBarcode, { BarcodeModeContext } from './TagBarcode'
import { todayDate, formatMoneyAuto } from 'helpers/helpers'
import { resolveBarcode, pxToMm, cleanBarcode } from 'helpers/barcode'
import logoIcon from 'assets/images/logo_icon.svg'

// ============================================================================
// PriceTag_3 (60x40) chapdagi vertikal (90° burilgan) shtrix-kod o'lchamlari.
// FAQAT 3-shablon uchun -- bu qiymatlar boshqa shablonlarga (1,2,4..9) ta'sir
// qilmaydi (ular o'z o'lchamlariga ega). Bemalol tuzatishingiz mumkin.
//
// TAG3_BARCODE_LEN_PX -- burilgach kod uzunligi VERTIKAL bo'ylab yotadi.
//   DIQQAT: yorliq balandligi 146px (ichkarisi ~134px) va overflow:hidden.
//   Bu qiymat 134px dan katta bo'lsa, kod yuqori-pastdan KESILADI (quiet zone
//   yo'qoladi) va skaner o'qiy olmaydi.
//   126 -- sig'adigan ENG KATTA o'lcham: chiziq ~0.28mm (skaner minimumi 0.25mm
//   dan yuqori), kod 134px ichiga chekkalari bilan to'liq joylashadi.
// ============================================================================
const TAG3_BARCODE_LEN_PX = 148
const TAG3_BAR_HEIGHT_PX = 42 // chiziq uzunligi = skaner nishonlash balandligi (uzunroq = oson o'qiladi)
// 3-shablon o'chirilgan (pastda comment'da) -- shu sabab bu qiymat hozir ishlatilmaydi,
// lekin shablonni qayta yoqqanda kerak bo'ladi.
// eslint-disable-next-line no-unused-vars
const TAG3_BARCODE_COL_PX = 60 // chap ustun eni (uzun chiziq + raqam sig'ishi uchun)

function PriceTag60x40({
  data,
  selectedPriceTag,
  selectPriceTag,
  printProducts,
  printRef,
  cashbox,
  a4 = false,
  activeDate = true,
  printSignal = 0,
}) {
  const [image, setImage] = useState(null)
  const fileInputRef = useRef(null)
  const barcodeMode = useContext(BarcodeModeContext)

  // eventlarni ortiqcha yubormaslik uchun
  const lastSelectedRef = useRef(null)
  const lastPrintSignalRef = useRef(printSignal)

  const fileUpload = (e) => {
    const file = e.target.files && e.target.files[0]
    if (file) setImage(URL.createObjectURL(file))
  }

  // Chegirma (chizilgan eski narx) yo'q bo'lsa yorliqda umuman ko'rinmasin.
  // Ilgari actionPrice bo'sh yoki 0 bo'lganda chizilgan "0" turib qolardi.
  function hasOldPrice(product) {
    return Number(product?.actionPrice) > 0
  }

  function getSeperatedPrice(price, index = 0) {
    const myArray = String(price).split(' ')
    if (index === 0) return myArray[0] || '0'
    if (index === 1) {
      if (myArray.length === 2) return myArray[1]
      if (myArray.length === 3) return myArray[1] + ' ' + myArray[2]
      return '0'
    }
    return '0'
  }

  // PriceTag_3 (60x40) — chapdagi vertikal shtrix-kod. Skaner ishonchli o'qishi
  // uchun resolveBarcode() formatni (haqiqiy EAN-13/EAN-8 -> o'z formati, aks holda
  // CODE128) va chiziq qalinligini avtomatik tanlaydi. margin=0, chunki quiet zone
  // modul hisobiga kiritilgan (helpers/barcode.js). Preview va print bir xil.
  //
  // 3-shablon hozir O'CHIRILGAN (pastda comment'da), shuning uchun bu funksiya
  // chaqirilmaydi. Shablonni qayta yoqqanda ishlaydi -- shu sabab saqlab qoldik.
  // eslint-disable-next-line no-unused-vars
  function renderTag3Barcode(product) {
    // Rejimni resolveBarcode opsiyalariga o'giramiz: 'auto' -> aqlli EAN/CODE128,
    // 'code128' -> hammasi CODE128, aks holda -> tanlangan aniq format.
    const resolveOpts =
      barcodeMode === 'code128'
        ? { code128Only: true }
        : barcodeMode && barcodeMode !== 'auto'
        ? { format: barcodeMode }
        : {}
    // Kodni tozalaymiz: backend uni bo'sh joy bilan qaytaradi ("4870254132743        ").
    // Tozalanmasa EAN-13 tekshiruvi yiqilib, CODE128 chizilib qolardi.
    const code = cleanBarcode(product?.barcode)
    const options = resolveBarcode(code, pxToMm(TAG3_BARCODE_LEN_PX), resolveOpts)
    if (!options) return null
    // marginTop qo'shilmaydi -- u kodni pastga surib, chekkasini kestirib qo'yadi.
    // Markazlashtirish ota-elementdagi flex (align/justify center) bilan bo'ladi.
    return (
      <div style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}>
        <TagBarcode
          value={code}
          format={options.format}
          lastChar={options.lastChar}
          width={options.width}
          height={TAG3_BAR_HEIGHT_PX}
          displayValue
          background="transparent"
          textMargin={1}
          margin={0}
          fontOptions="bold"
          fontSize={9}
        />
      </div>
    )
  }

  // PriceTag_7 (60x40) — do'kon nomi (yuqorida), chapda mahsulot + shtrix-kod,
  // o'ngda optom va dona narxlari ramkali quticha ichida (rasmdagi shablon).
  // Preview va print uchun bir xil ishlatiladi.
  function renderDesign7(product) {
    const currency = product?.currencyName || cashbox?.defaultCurrency || "So'm"
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', padding: '6px 8px' }}>
        {/* Sarlavha: do'kon nomi */}
        <div
          style={{
            textAlign: 'center',
            fontWeight: 800,
            fontSize: 14,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            lineHeight: 1.1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {product?.posName}
        </div>

        {/* Tana: chapda mahsulot + shtrix-kod, o'ngda narxlar */}
        <div style={{ display: 'flex', flex: 1, gap: 6, overflow: 'hidden', marginTop: 4 }}>
          {/* Chap ustun */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                textTransform: 'uppercase',
                lineHeight: 1.05,
                marginTop: 8,
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {product?.productName}
            </div>
            <div style={{ marginTop: 'auto', maxWidth: '100%', overflow: 'hidden' }}>
              <TagBarcode
                value={product?.barcode || ''}
                width={1}
                height={22}
                margin={2}
                background="transparent"
                textMargin={0}
                fontOptions="bold"
                fontSize={10}
              />
            </div>
          </div>

          {/* O'ng ustun */}
          <div style={{ width: 86, flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ fontSize: 8, fontWeight: 800, textTransform: 'uppercase' }}>Optom narx</div>
            <div style={{ border: '1px solid #000', borderRadius: 5, padding: '3px 5px', marginBottom: 5 }}>
              <b style={{ fontSize: 16, whiteSpace: 'nowrap' }}>{formatMoneyAuto(product?.wholesalePrice || 0)}</b>
              <span style={{ fontSize: 7, display: 'block', lineHeight: 1 }}>{currency}</span>
            </div>

            <div style={{ fontSize: 8, fontWeight: 800, textTransform: 'uppercase' }}>Dona narx</div>
            <div style={{ border: '1px solid #000', borderRadius: 5, padding: '3px 5px' }}>
              <b style={{ fontSize: 16, whiteSpace: 'nowrap' }}>{formatMoneyAuto(product?.salePrice || 0)}</b>
              <span style={{ fontSize: 7, display: 'block', lineHeight: 1 }}>{currency}</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // PriceTag_8 (60x40) — yuqorida mahsulot nomi (markazda), ostida yonma-yon
  // optom va dona narxlari ramkali quticha ichida (rasmdagi shablon).
  // Preview va print uchun bir xil ishlatiladi.
  function renderDesign8(product) {
    const currency = product?.currencyName || cashbox?.defaultCurrency || "So'm"
    const priceBox = (label, value) => (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
        <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
        <div
          style={{
            border: '1px solid #000',
            borderRadius: 6,
            padding: '4px 4px 3px',
            textAlign: 'center',
            width: '100%',
          }}
        >
          <div style={{ fontSize: 19, fontWeight: 800, whiteSpace: 'nowrap', lineHeight: 1 }}>
            {formatMoneyAuto(value || 0)}
          </div>
          <div style={{ fontSize: 8, marginTop: 1 }}>{currency}</div>
        </div>
      </div>
    )
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', padding: '8px 8px 6px' }}>
        <div
          style={{
            textAlign: 'center',
            fontWeight: 800,
            fontSize: 14,
            textTransform: 'uppercase',
            lineHeight: 1.1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {product?.productName}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 'auto', marginBottom: 'auto' }}>
          {priceBox('Optom narx', product?.wholesalePrice)}
          {priceBox('Dona narx', product?.salePrice)}
        </div>
      </div>
    )
  }

  // PriceTag_9 (60x40) — NARXSIZ: do'kon nomi (yuqorida), mahsulot nomi (markazda),
  // pastda shtrix-kod. Narx ko'rsatilmaydi. Preview va print uchun bir xil.
  function renderDesign9(product) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          padding: '8px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            fontWeight: 800,
            fontSize: 10,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            lineHeight: 1.1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%',
          }}
        >
          {product?.posName}
        </div>

        <div
          style={{
            textAlign: 'center',
            fontWeight: 800,
            fontSize: 13,
            lineHeight: 1.15,
            textTransform: 'uppercase',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            width: '100%',
          }}
        >
          {product?.productName}
        </div>

        <div className="d-flex justify-content-center" style={{ width: '100%' }}>
          <TagBarcode
            value={product?.barcode || ''}
            width={1}
            height={24}
            background="transparent"
            textMargin={0}
            fontOptions="bold"
            fontSize={11}
          />
        </div>
      </div>
    )
  }

  const selectedKey = useMemo(() => {
    if (!selectedPriceTag) return null
    const key = Object.keys(selectedPriceTag).find((k) => selectedPriceTag[k])
    return key || null
  }, [selectedPriceTag])

  const onSelect = (designKey) => {
    selectPriceTag(designKey)

    // bir xil dizaynni qayta-qayta bosganda eventni ko‘paytirmaymiz
    const fingerprint = `${designKey}:${data?.barcode || ''}:${data?.barcodeScales || ''}`
    if (lastSelectedRef.current === fingerprint) return
    lastSelectedRef.current = fingerprint
  }

  useEffect(() => {
    if (printSignal === lastPrintSignalRef.current) return
    lastPrintSignalRef.current = printSignal

    if (!selectedKey || !selectedKey.startsWith('priceTag60x40')) return
  }, [printSignal, selectedKey])

  return (
    <>
      {/* Logotip yuklash uchun umumiy yashirin input (PriceTag_4 va PriceTag_6 uchun) */}
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={fileUpload} />

      <div className="d-flex flex-wrap">
        {/* PriceTag_1 (60x40) */}
        <div
          className={
            'price_tag_body_60x40 price-tag-border mb-3 me-3 ' +
            (selectedPriceTag?.priceTag60x40_1 ? 'border-primary' : '')
          }
          onClick={() => onSelect('priceTag60x40_1')}
        >
          <div className="d-flex justify-content-between price_tag_body_60x40_1_barcode">
            <TagBarcode
              value={data?.barcode || ''}
              width={1}
              height={16}
              background="transparent"
              textMargin={0}
              fontOptions="bold"
              fontSize={10}
            />
            {hasOldPrice(data) && (
              <div className="price-tag-price-wrapper">
                <div>
                  <b className="fz20">{formatMoneyAuto(data?.actionPrice)}</b>
                </div>
                <hr className="price-tag-throw-line" />
              </div>
            )}
          </div>

          <div className="d-flex justify-content-center price_tag_body_60x40_1_price">
            <div className="d-flex fz30">
              <div className="vertical-center">
                <span className="price_tag_body_60x40_1_price_left">
                  {getSeperatedPrice(formatMoneyAuto(data?.salePrice || 0), 0)}
                </span>
              </div>
              <div className="price_tag_body_60x40_1_price_right">
                <div>{data?.currencyName || cashbox?.defaultCurrency}</div>
                <div className="text-center">
                  {getSeperatedPrice(formatMoneyAuto(data?.salePrice || 0), 1)}
                </div>
              </div>
            </div>
          </div>

          <div className="vertical-center">
            <div className="price_tag_body_60x40_1_name">{data?.productName}</div>
          </div>

          {data?.barcodeScales && (
            <div className="tag-plu">
              PLU: {data?.barcodeScales}
            </div>
          )}
          <div className="fz10 date">{todayDate()}</div>
        </div>

        {/* PriceTag_2 (60x40) */}
        <div
          className={
            'price_tag_body_60x40 price-tag-border mb-3 me-3 ' +
            (selectedPriceTag?.priceTag60x40_2 ? 'border-primary' : '')
          }
          onClick={() => onSelect('priceTag60x40_2')}
        >
          <div className="vertical-center" style={{ textAlign: 'center', padding: '6px 8px' }}>
            <div style={{ fontWeight: 800, fontSize: 15, letterSpacing: 1 }}>
              {data?.posName}
            </div>
            {data?.productName && (
              <div
                style={{
                  fontSize: 12,
                  marginTop: 4,
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {data?.productName}
              </div>
            )}
          </div>

          <div className="d-flex justify-content-center align-items-end" style={{ marginTop: 3 }}>
            <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1 }}>
              {formatMoneyAuto(data?.salePrice || 0)}
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, marginLeft: 8 }}>
              {data?.currencyName || cashbox?.defaultCurrency}
            </div>
          </div>

          <div className="d-flex justify-content-center" style={{ marginTop: 0 }}>
            <TagBarcode
              value={data?.barcode || ''}
              width={1}
              height={26}
              background="transparent"
              textMargin={0}
              fontOptions="bold"
              fontSize={12}
              margin={0}
              marginBottom={4}
            />
          </div>

          {data?.barcodeScales && (
            <div className="tag-plu">
              PLU: {data?.barcodeScales}
            </div>
          )}
          <div className="fz10 date">{todayDate()}</div>
        </div>

        {/* PriceTag_3 (60x40) — O'CHIRILGAN (ko'rinish bloki).
            Chapdagi vertikal (90° burilgan) shtrix-kod 60x40 yorliqqa yaxshi
            joylashmagani uchun bu shablon ro'yxatda ko'rsatilmaydi.
            Qayta yoqish: shu izohni (bu qator va eng pastdagi yopuvchi qatorni)
            olib tashlang. Chop etish bloki ham xuddi shunday o'chirilgan.

        <div
          className={
            'price_tag_body_60x40 price-tag-border mb-3 me-3 d-flex ' +
            (selectedPriceTag?.priceTag60x40_3 ? 'border-primary' : '')
          }
          onClick={() => onSelect('priceTag60x40_3')}
          style={{ padding: 6 }}
        >
          <div style={{ width: TAG3_BARCODE_COL_PX, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {renderTag3Barcode(data)}
          </div>

          <div style={{ width: 2, backgroundColor: '#000', alignSelf: 'stretch', marginLeft: 6, marginRight: 8 }} />

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ textAlign: 'center', fontSize: 12, lineHeight: 1 }}>
              {data?.productName && <div className="price_tag_body_60x30_1_name">{data?.productName}</div>}
            </div>

            <div style={{ width: '100%', height: 1, backgroundColor: '#000', margin: '6px 0' }} />

            <div className="d-flex justify-content-between align-items-end" style={{ width: '100%' }}>
              <div style={{ fontSize: 24, fontWeight: 800 }}>{formatMoneyAuto(data?.salePrice || 0)}</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{cashbox?.defaultCurrency}</div>
            </div>

            <div style={{ width: '100%', height: 1, backgroundColor: '#000', margin: '6px 0' }} />

            {data?.barcodeScales && (
              <div style={{ marginTop: 'auto', fontSize: 20, fontWeight: 600 }}>
                PLU: {data?.barcodeScales}
              </div>
            )}
          </div>
        </div>
        */}

        {/* PriceTag_4 (60x40) */}
        <div
          className={
            'price_tag_body_60x40 price-tag-border mb-3 me-3 ' +
            (selectedPriceTag?.priceTag60x40_4 ? 'border-primary' : '')
          }
          onClick={() => onSelect('priceTag60x40_4')}
        >
          <div style={{ display: 'flex', width: '100%' }}>
            <div
              style={{
                width: 76,
                height: 60,
                display: 'flex',
                alignItems: 'center',
                borderRight: '1px solid gray',
                borderBottom: '1px solid gray',
                justifyContent: 'center',
                borderRadius: 0,
                background: '#fff',
                flexDirection: 'column',
              }}
            >
              <img
                src={image || logoIcon}
                width="60"
                height="40"
                alt="logo"
                style={{ objectFit: 'contain', borderRadius: 2, cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation()
                  fileInputRef.current && fileInputRef.current.click()
                }}
              />
            </div>

            <div style={{ flex: 1, paddingLeft: 8, paddingRight: 8, paddingTop: 10 }}>
              <div className="price_tag_body_60x30_1_name">{data?.productName}</div>
            </div>
          </div>

          <div style={{ display: 'block', alignItems: 'center', padding: '0 8px 8px 8px' }}>
            {/* Narx va valyuta yonma-yon: "100 000 So'm" */}
            <div className="d-flex align-items-end">
              <div style={{ fontSize: 24, fontWeight: 800 }}>{formatMoneyAuto(data?.salePrice || 0)}</div>
              <div style={{ fontSize: 20, fontWeight: 700, marginLeft: 6 }}>{data?.currencyName || cashbox?.defaultCurrency || "So'm"}</div>
            </div>

            {/* Shtrix-kod, PLU esa uning yonida kichik shrift bilan */}
            <div className="d-flex align-items-center">
              <div>
                <TagBarcode
                  value={data?.barcode || ''}
                  width={1}
                  height={24}
                  background="transparent"
                  textMargin={0}
                  fontOptions="bold"
                  fontSize={10}
                />
              </div>
              {data?.barcodeScales && (
                <div style={{ fontSize: 10, fontWeight: 600, marginLeft: 6, whiteSpace: 'nowrap' }}>
                  PLU: {data?.barcodeScales}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* PriceTag_5 (60x40) — Market nomi, mahsulot nomi, katta shtrix-kod raqamlari */}
        <div
          className={
            'price_tag_body_60x40 price-tag-border mb-3 me-3 ' +
            (selectedPriceTag?.priceTag60x40_5 ? 'border-primary' : '')
          }
          onClick={() => onSelect('priceTag60x40_5')}
        >
          <div className="vertical-center" style={{ textAlign: 'center', padding: '4px 8px' }}>
            <div style={{ fontWeight: 800, fontSize: 14, letterSpacing: 0.5, lineHeight: 1.2 }}>
              {data?.posName}
            </div>
            {data?.productName && (
              <div
                style={{
                  fontSize: 11,
                  marginTop: 3,
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {data?.productName}
              </div>
            )}
          </div>

          <div className="d-flex justify-content-center align-items-end" style={{ marginTop: 4 }}>
            <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>
              {formatMoneyAuto(data?.salePrice || 0)}
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, marginLeft: 6 }}>
              {data?.currencyName || cashbox?.defaultCurrency}
            </div>
          </div>

          <div className="d-flex justify-content-center flex-column align-items-center" style={{ marginTop: 6 }}>
            <TagBarcode
              value={data?.barcode || ''}
              width={1}
              height={24}
              background="transparent"
              textMargin={2}
              fontOptions="bold"
              fontSize={16}
            />
          </div>

          {data?.barcodeScales && (
            <div className="tag-plu">
              PLU: {data?.barcodeScales}
            </div>
          )}
          <div className="fz10 date">{todayDate()}</div>
        </div>

        {/* PriceTag_6 (60x40) — Logotip + mahsulot nomi + shtrix-kod (quti yorlig'i dizayni) */}
        <div
          className={
            'price_tag_body_60x40 price-tag-border mb-3 me-3 ' +
            (selectedPriceTag?.priceTag60x40_6 ? 'border-primary' : '')
          }
          onClick={() => onSelect('priceTag60x40_6')}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '5px 6px',
          }}
        >
          <div
            style={{ height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation()
              fileInputRef.current && fileInputRef.current.click()
            }}
          >
            {image ? (
              <img
                src={image}
                alt="logo"
                style={{ maxHeight: 72, maxWidth: 280, objectFit: 'contain' }}
              />
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  border: '1px dashed #9aa0a6',
                  borderRadius: 4,
                  padding: '3px 8px',
                  fontSize: 9,
                  color: '#6b7075',
                  whiteSpace: 'nowrap',
                }}
              >
                <i className="uil uil-image-upload" style={{ fontSize: 12 }}></i>
                Logo yuklash
              </div>
            )}
          </div>

          <div
            style={{
              fontWeight: 800,
              fontSize: 12,
              lineHeight: 1.1,
              textAlign: 'center',
              textTransform: 'uppercase',
              width: '100%',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {data?.productName}
          </div>

          <div className="d-flex justify-content-center" style={{ width: '100%' }}>
            <TagBarcode
              value={data?.barcode || ''}
              width={1.4}
              height={22}
              background="transparent"
              textMargin={0}
              fontOptions="bold"
              fontSize={11}
            />
          </div>
        </div>

        {/* PriceTag_7 (60x40) — IDOKON logotip + optom va dona narxlari */}
        <div
          className={
            'price_tag_body_60x40 price-tag-border mb-3 me-3 ' +
            (selectedPriceTag?.priceTag60x40_7 ? 'border-primary' : '')
          }
          onClick={() => onSelect('priceTag60x40_7')}
        >
          {renderDesign7(data)}
        </div>

        {/* PriceTag_8 (60x40) — mahsulot nomi (markazda) + yonma-yon optom va dona narxlari */}
        <div
          className={
            'price_tag_body_60x40 price-tag-border mb-3 me-3 ' +
            (selectedPriceTag?.priceTag60x40_8 ? 'border-primary' : '')
          }
          onClick={() => onSelect('priceTag60x40_8')}
        >
          {renderDesign8(data)}
        </div>

        {/* PriceTag_9 (60x40) — NARXSIZ: do'kon nomi + mahsulot nomi + shtrix-kod */}
        <div
          className={
            'price_tag_body_60x40 price-tag-border mb-3 me-3 ' +
            (selectedPriceTag?.priceTag60x40_9 ? 'border-primary' : '')
          }
          onClick={() => onSelect('priceTag60x40_9')}
        >
          {renderDesign9(data)}
        </div>
      </div>

      {/* PRINT HTML (hidden) */}
      <div className="d-none">
        {selectedPriceTag?.priceTag60x40_1 && (
          <div ref={printRef} className={'price-tag-wrap ' + (a4 ? 'a4 ' : ' ') + (activeDate ? ' ' : 'hide-date ')}>
            {printProducts.map((product, index) => (
              <div className="price_tag_body_60x40 page-breaker" key={index} onClick={() => onSelect('priceTag60x40_1')}>
                <div className="d-flex justify-content-between barcode">
                  <TagBarcode value={product?.barcode || ''} width={1} height={16} background="transparent" textMargin={0} fontOptions="bold" fontSize={10} />
                  {hasOldPrice(product) && (
                    <div className="price-tag-price-wrapper">
                      <div>
                        <b className="fz22">{formatMoneyAuto(product?.actionPrice)}</b>
                      </div>
                      <hr className="price-tag-throw-line" />
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-center price_tag_body_60x40_1_price">
                  <div className="d-flex fz30">
                    <div className="vertical-center">
                      <span className="price_tag_body_60x40_1_price_left">
                        {getSeperatedPrice(formatMoneyAuto(product?.salePrice || 0), 0)}
                      </span>
                    </div>
                    <div className="price_tag_body_60x40_1_price_right">
                      <div>{product?.currencyName || cashbox?.defaultCurrency}</div>
                      <div className="text-center">
                        {getSeperatedPrice(formatMoneyAuto(product?.salePrice || 0), 1)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="vertical-center">
                  <div className="price_tag_body_60x40_1_name">{product?.productName}</div>
                </div>

                {product?.barcodeScales && (
                  <div className="tag-plu">
                    PLU: {product?.barcodeScales}
                  </div>
                )}
                <div className="fz10 date">{todayDate()}</div>
              </div>
            ))}
          </div>
        )}

        {selectedPriceTag?.priceTag60x40_2 && (
          <div ref={printRef} className={'price-tag-wrap ' + (a4 ? 'a4 ' : ' ') + (activeDate ? ' ' : 'hide-date ')}>
            {printProducts.map((product, index) => (
              <div className="price_tag_body_60x40 page-breaker" key={'alt-' + index} onClick={() => onSelect('priceTag60x40_2')}>
                <div className="vertical-center" style={{ textAlign: 'center', padding: '6px 8px' }}>
                  <div style={{ fontWeight: 800, fontSize: 15, letterSpacing: 1 }}>
                    {cashbox?.organizationName || product?.posName || product?.posName}
                  </div>
                  {product?.productName && (
                    <div style={{ fontSize: 12, marginTop: 4, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {product?.productName}
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-center align-items-end" style={{ marginTop: 3 }}>
                  <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1 }}>{formatMoneyAuto(product?.salePrice || 0)}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, marginLeft: 8 }}>{product?.currencyName || cashbox?.defaultCurrency}</div>
                </div>

                <div className="d-flex justify-content-center" style={{ marginTop: 0 }}>
                  <TagBarcode value={product?.barcode || ''} width={1} height={26} background="transparent" textMargin={0} fontOptions="bold" fontSize={12} margin={0} marginBottom={4} />
                </div>

                {product?.barcodeScales && (
                  <div className="tag-plu">
                    PLU: {product?.barcodeScales}
                  </div>
                )}
                <div className="fz10 date">{todayDate()}</div>
              </div>
            ))}
          </div>
        )}

        {/* PriceTag_3 (60x40) — O'CHIRILGAN (chop etish bloki).
            Ko'rinish bloki bilan birga o'chirilgan. Qayta yoqishda ikkalasini
            ham tiklang, aks holda shablon ko'rinadi-yu, chop etilmaydi.

        {selectedPriceTag?.priceTag60x40_3 && (
          <div ref={printRef} className={'price-tag-wrap ' + (a4 ? 'a4 ' : ' ') + (activeDate ? ' ' : 'hide-date ')}>
            {printProducts.map((product, index) => (
              <div className="price_tag_body_60x40 page-breaker" key={'t3-' + index} onClick={() => onSelect('priceTag60x40_3')} style={{ padding: 6 }}>
                <div style={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
                  <div style={{ width: TAG3_BARCODE_COL_PX, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {renderTag3Barcode(product)}
                  </div>

                  <div style={{ width: 2, backgroundColor: '#000', marginLeft: 6, marginRight: 8 }} />

                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingLeft: 6, paddingRight: 8 }}>
                    <div style={{ textAlign: 'center', fontSize: 10, lineHeight: 1.1 }}>
                      {product?.productName && <div className="price_tag_body_60x30_1_name">{product?.productName}</div>}
                    </div>

                    <div style={{ width: '100%', height: 1, backgroundColor: '#000', margin: '6px 0' }} />

                    <div className="d-flex justify-content-between align-items-end" style={{ width: '100%' }}>
                      <div style={{ fontSize: 24, fontWeight: 800 }}>{formatMoneyAuto(product?.salePrice || 0)}</div>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>{cashbox?.defaultCurrency}</div>
                    </div>

                    <div style={{ width: '100%', height: 1, backgroundColor: '#000', margin: '6px 0' }} />

                    {product?.barcodeScales && (
                      <div style={{ marginTop: 'auto', fontSize: 10, fontWeight: 600 }}>
                        PLU: {product?.barcodeScales}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        */}

        {selectedPriceTag?.priceTag60x40_4 && (
          <div ref={printRef} className={'price-tag-wrap ' + (a4 ? 'a4 ' : ' ') + (activeDate ? ' ' : 'hide-date ')}>
            {printProducts.map((product, index) => (
              <div className="price_tag_body_60x40 page-breaker" key={'t4-' + index} onClick={() => onSelect('priceTag60x40_4')} style={{ padding: 8 }}>
                <div style={{ display: 'flex', width: '100%' }}>
                  <div style={{ width: 76, height: 60, display: 'flex', alignItems: 'center', borderRight: '1px solid gray', borderBottom: '1px solid gray', justifyContent: 'center', borderRadius: 0, background: '#fff', flexDirection: 'column' }}>
                    <img src={image || logoIcon} width="60" height="40" alt="logo" style={{ objectFit: 'contain', borderRadius: 2 }} />
                  </div>

                  <div style={{ flex: 1, paddingLeft: 8, paddingRight: 8, paddingTop: 10 }}>
                    <div className="price_tag_body_60x30_1_name">{product?.productName}</div>
                  </div>
                </div>

                <div style={{ display: 'block', alignItems: 'center', padding: '0 8px 8px 8px' }}>
                  {/* Narx va valyuta yonma-yon: "100 000 So'm" */}
                  <div className="d-flex align-items-end">
                    <div style={{ fontSize: 24, fontWeight: 800 }}>{formatMoneyAuto(product?.salePrice || 0)}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, marginLeft: 6 }}>{product?.currencyName || cashbox?.defaultCurrency || "So'm"}</div>
                  </div>

                  {/* Shtrix-kod, PLU esa uning yonida kichik shrift bilan */}
                  <div className="d-flex align-items-center">
                    <div>
                      <TagBarcode value={product?.barcode || ''} width={1} height={24} background="transparent" textMargin={0} fontOptions="bold" fontSize={10} />
                    </div>
                    {product?.barcodeScales && (
                      <div style={{ fontSize: 14, fontWeight: 600, marginLeft: 6, whiteSpace: 'nowrap' }}>
                        PLU: {product?.barcodeScales}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedPriceTag?.priceTag60x40_5 && (
          <div ref={printRef} className={'price-tag-wrap ' + (a4 ? 'a4 ' : ' ') + (activeDate ? ' ' : 'hide-date ')}>
            {printProducts.map((product, index) => (
              <div className="price_tag_body_60x40 page-breaker" key={'t5-' + index} onClick={() => onSelect('priceTag60x40_5')} style={{ padding: 8 }}>
                <div className="vertical-center" style={{ textAlign: 'center', padding: '4px 8px' }}>
                  <div style={{ fontWeight: 800, fontSize: 14, letterSpacing: 0.5, lineHeight: 1.2 }}>
                    {cashbox?.organizationName || product?.posName}
                  </div>
                  {product?.productName && (
                    <div style={{ fontSize: 11, marginTop: 3, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {product?.productName}
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-center align-items-end" style={{ marginTop: 4 }}>
                  <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>{formatMoneyAuto(product?.salePrice || 0)}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, marginLeft: 6 }}>{product?.currencyName || cashbox?.defaultCurrency}</div>
                </div>

                <div className="d-flex justify-content-center flex-column align-items-center" style={{ marginTop: 6 }}>
                  <TagBarcode
                    value={product?.barcode || ''}
                    width={1}
                    height={24}
                    background="transparent"
                    textMargin={2}
                    fontOptions="bold"
                    fontSize={16}
                  />
                </div>

                {product?.barcodeScales && (
                  <div className="tag-plu">
                    PLU: {product?.barcodeScales}
                  </div>
                )}
                <div className="fz10 date">{todayDate()}</div>
              </div>
            ))}
          </div>
        )}

        {selectedPriceTag?.priceTag60x40_6 && (
          <div ref={printRef} className={'price-tag-wrap ' + (a4 ? 'a4 ' : ' ') + (activeDate ? ' ' : 'hide-date ')}>
            {printProducts.map((product, index) => (
              <div
                className="price_tag_body_60x40 page-breaker"
                key={'t6-' + index}
                onClick={() => onSelect('priceTag60x40_6')}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '5px 6px',
                }}
              >
                <div style={{ height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {image && (
                    <img
                      src={image}
                      alt="logo"
                      style={{ maxHeight: 72, maxWidth: 280, objectFit: 'contain' }}
                    />
                  )}
                </div>

                <div
                  style={{
                    fontWeight: 800,
                    fontSize: 12,
                    lineHeight: 1.1,
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    width: '100%',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {product?.productName}
                </div>

                <div className="d-flex justify-content-center" style={{ width: '100%' }}>
                  <TagBarcode
                    value={product?.barcode || ''}
                    width={1.4}
                    height={22}
                    background="transparent"
                    textMargin={0}
                    fontOptions="bold"
                    fontSize={11}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedPriceTag?.priceTag60x40_7 && (
          <div ref={printRef} className={'price-tag-wrap ' + (a4 ? 'a4 ' : ' ') + (activeDate ? ' ' : 'hide-date ')}>
            {printProducts.map((product, index) => (
              <div className="price_tag_body_60x40 page-breaker" key={'t7-' + index} onClick={() => onSelect('priceTag60x40_7')}>
                {renderDesign7(product)}
              </div>
            ))}
          </div>
        )}

        {selectedPriceTag?.priceTag60x40_8 && (
          <div ref={printRef} className={'price-tag-wrap ' + (a4 ? 'a4 ' : ' ') + (activeDate ? ' ' : 'hide-date ')}>
            {printProducts.map((product, index) => (
              <div className="price_tag_body_60x40 page-breaker" key={'t8-' + index} onClick={() => onSelect('priceTag60x40_8')}>
                {renderDesign8(product)}
              </div>
            ))}
          </div>
        )}

        {selectedPriceTag?.priceTag60x40_9 && (
          <div ref={printRef} className={'price-tag-wrap ' + (a4 ? 'a4 ' : ' ') + (activeDate ? ' ' : 'hide-date ')}>
            {printProducts.map((product, index) => (
              <div className="price_tag_body_60x40 page-breaker" key={'t9-' + index} onClick={() => onSelect('priceTag60x40_9')}>
                {renderDesign9(product)}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export default PriceTag60x40
